import * as React from 'react';
import {
  columnVisibilityFeature,
  rowSelectionFeature,
  tableFeatures,
  useTable,
  type ColumnDef,
  type ColumnVisibilityState,
  type RowSelectionState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { Checkbox } from '../../primitives/checkbox.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../primitives/table.js';
import { cn } from '../../lib/cn.js';
import { formatValue } from '../format.js';
import { toggleSort } from '../list-state.js';
import type { ConfirmSpec, ListState, ResourceColumn, RowAction } from '../types.js';
import { RowActions } from './row-actions.js';

/**
 * Sorting, filtering and pagination are all server-side, so the table instance
 * only owns what is genuinely client state: which columns are visible and which
 * rows are selected.
 */
const features = tableFeatures({ columnVisibilityFeature, rowSelectionFeature });

/**
 * TanStack constrains its row type to `Record<string, any> | any[]`. Pushing
 * that constraint up into `ResourceDefinition` would tax every resource author
 * for an internal detail, so the interop is a cast confined to this file: the
 * kit's own `TItem` stays free, and `row.original` is cast back on read.
 */
type TableRowData = Record<string, unknown>;

/** Selection is `Record<string, true>` — deselecting removes the key. */
function withSelection(
  selection: RowSelectionState,
  id: string,
  selected: boolean,
): RowSelectionState {
  if (!selected) {
    const { [id]: _removed, ...rest } = selection;
    return rest;
  }
  return { ...selection, [id]: true };
}

export interface DataTableProps<TItem> {
  columns: readonly ResourceColumn<TItem>[];
  rows: readonly TItem[];
  getRowId: (row: TItem) => string;
  state: ListState;
  onChange: (patch: Partial<ListState>) => void;
  hiddenColumns: ReadonlySet<string>;
  selection: RowSelectionState;
  onSelectionChange: (next: RowSelectionState) => void;
  selectable: boolean;
  rowActions?: readonly RowAction<TItem>[];
  onRowClick?: (row: TItem) => void;
  onConfirm: (
    spec: ConfirmSpec,
    tone: 'default' | 'danger',
    run: () => void | Promise<void>,
  ) => void;
}

function readValue<TItem>(column: ResourceColumn<TItem>, row: TItem): unknown {
  if (column.accessor) return column.accessor(row);
  return (row as Record<string, unknown>)[column.id];
}

export function DataTable<TItem>({
  columns,
  rows,
  getRowId,
  state,
  onChange,
  hiddenColumns,
  selection,
  onSelectionChange,
  selectable,
  rowActions,
  onRowClick,
  onConfirm,
}: DataTableProps<TItem>) {
  const columnVisibility = React.useMemo<ColumnVisibilityState>(
    () => Object.fromEntries([...hiddenColumns].map((id) => [id, false])),
    [hiddenColumns],
  );

  const tableColumns = React.useMemo<ColumnDef<typeof features, TableRowData, unknown>[]>(
    () =>
      columns.map((column) => ({
        id: column.id,
        header: column.header,
        cell: ({ row }) => {
          const item = row.original as TItem;
          const value = readValue(column, item);
          return column.cell ? column.cell({ row: item, value }) : formatValue(value);
        },
      })),
    [columns],
  );

  const data = React.useMemo(() => rows as readonly TableRowData[], [rows]);

  const table = useTable({
    features,
    data,
    columns: tableColumns,
    getRowId: (row) => getRowId(row as TItem),
    enableRowSelection: selectable,
    state: { columnVisibility, rowSelection: selection },
    onRowSelectionChange: (updater) => {
      onSelectionChange(typeof updater === 'function' ? updater(selection) : updater);
    },
  });

  const visibleColumns = columns.filter((column) => !hiddenColumns.has(column.id));
  const showActions = (rowActions?.length ?? 0) > 0;
  const allSelected = rows.length > 0 && rows.every((row) => selection[getRowId(row)]);
  const someSelected = rows.some((row) => selection[getRowId(row)]);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {selectable && (
            <TableHead>
              <Checkbox
                aria-label="Seleziona tutte le righe"
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={(checked) => {
                  if (checked === true) {
                    onSelectionChange(
                      Object.fromEntries(rows.map((row) => [getRowId(row), true as const])),
                    );
                  } else {
                    onSelectionChange({});
                  }
                }}
              />
            </TableHead>
          )}
          {visibleColumns.map((column) => {
            const sortKey = column.sortKey;
            const active = sortKey !== undefined && state.sort === sortKey;
            return (
              <TableHead
                key={column.id}
                style={column.width ? { width: column.width } : undefined}
                className={cn(column.align === 'right' && 'text-right')}
                aria-sort={
                  active ? (state.order === 'asc' ? 'ascending' : 'descending') : undefined
                }
              >
                {sortKey === undefined ? (
                  column.header
                ) : (
                  <button
                    type="button"
                    className={cn(
                      'inline-flex items-center gap-1 rounded-control transition-colors hover:text-ink-2',
                      active && 'text-accent',
                    )}
                    onClick={() => onChange(toggleSort(state, sortKey))}
                  >
                    {column.header}
                    {!active && <ChevronsUpDown className="size-3 opacity-50" />}
                    {active && state.order === 'asc' && <ArrowUp className="size-3" />}
                    {active && state.order === 'desc' && <ArrowDown className="size-3" />}
                  </button>
                )}
              </TableHead>
            );
          })}
          {showActions && <TableHead className="w-0" />}
        </TableRow>
      </TableHeader>

      <TableBody>
        {table.getRowModel().rows.map((row) => {
          const id = row.id;
          const item = row.original as TItem;
          return (
            <TableRow
              key={id}
              data-state={selection[id] ? 'selected' : undefined}
              className={onRowClick ? 'cursor-pointer' : undefined}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
            >
              {selectable && (
                <TableCell onClick={(event) => event.stopPropagation()}>
                  <Checkbox
                    aria-label="Seleziona riga"
                    checked={Boolean(selection[id])}
                    onCheckedChange={(checked) =>
                      onSelectionChange(withSelection(selection, id, checked === true))
                    }
                  />
                </TableCell>
              )}
              {row.getVisibleCells().map((cell) => {
                const column = columns.find((candidate) => candidate.id === cell.column.id);
                return (
                  <TableCell
                    key={cell.id}
                    className={cn(column?.align === 'right' && 'text-right')}
                  >
                    <table.FlexRender cell={cell} />
                  </TableCell>
                );
              })}
              {showActions && (
                <TableCell onClick={(event) => event.stopPropagation()}>
                  <RowActions row={item} actions={rowActions ?? []} onConfirm={onConfirm} />
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
